import * as React from 'react';
import { useSelector } from 'react-redux';
import { Progress, Button } from 'reactstrap';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping, normalizeHeader } from '../../../Clients/ImportClients/autoMap';
import { getEntitiesDataItems } from '../../../../store/entities/selectors';
import { ClientEntity, ProfessionalEntity, AppointmentEntity } from '../../../../store/entities/state';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { ApplicationState } from '../../../../store';
import { createClientAsync, createAppointmentsAsync } from '../../../../ajax/requests';
import { AppointmentData } from '../../../../models/api-models';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';
import {
    parseAppointmentRow,
    ParseContext,
    ParsedAppointmentRow,
    RowOutcome,
    resolveEndTime,
    resolveRoom,
} from './parseAppointmentRow';

const BATCH_SIZE = 50;

interface ImportError {
    rowIndex: number;
    clientName: string | null;
    message: string;
    appointmentData?: AppointmentData;
}

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    options: ImportAppointmentOptions;
    onClose: () => void;
}

type ImportPhase = 'running' | 'done' | 'cancelled';

const AppointmentImportStep: React.FC<Props> = ({ result, mapping, options, onClose }) => {
    const t = useTranslations();
    const clients = useSelector<ApplicationState, ClientEntity[]>(getEntitiesDataItems<ClientEntity>('clients'));
    const professionals = useSelector<ApplicationState, ProfessionalEntity[]>(getEntitiesDataItems<ProfessionalEntity>('professionals'));
    const appointments = useSelector<ApplicationState, AppointmentEntity[]>(getEntitiesDataItems<AppointmentEntity>('appointments'));
    const generalSettings = useSelector(getGeneralSettings);

    // Stable refs — never trigger re-runs of the import effect
    const clientsRef = React.useRef(clients);
    clientsRef.current = clients;
    const professionalsRef = React.useRef(professionals);
    professionalsRef.current = professionals;
    const appointmentsRef = React.useRef(appointments);
    appointmentsRef.current = appointments;
    const generalSettingsRef = React.useRef(generalSettings);
    generalSettingsRef.current = generalSettings;

    const [phase, setPhase] = React.useState<ImportPhase>('running');
    const [currentAction, setCurrentAction] = React.useState('');
    const [total, setTotal] = React.useState(0);
    const [processed, setProcessed] = React.useState(0);
    const [created, setCreated] = React.useState(0);
    const [skipped, setSkipped] = React.useState(0);
    const [errors, setErrors] = React.useState<ImportError[]>([]);
    const [retrying, setRetrying] = React.useState<number | null>(null);

    const cancelledRef = React.useRef(false);

    React.useEffect(() => {
        runImport();
        return () => { cancelledRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function runImport() {
        const gs = generalSettingsRef.current;
        const serviceNames: Record<string, string> = {};
        for (const [key, s] of Object.entries(gs?.services ?? {})) {
            serviceNames[key] = s.name || key;
        }

        const ctx: ParseContext = {
            mapping,
            options,
            clients: clientsRef.current,
            professionals: professionalsRef.current,
            generalSettings: gs,
            serviceNames,
        };

        const parsed = result.allRows.map((row, i) => parseAppointmentRow(row, i, ctx));
        const toImport = parsed.filter(r => r.outcome === 'create' || r.outcome === 'new-client');
        setTotal(toImport.length);

        // Build dedup set from currently loaded appointments
        const dedupSet = new Set<string>();
        for (const apt of appointmentsRef.current) {
            const key = makeDedupKey(apt.date, apt.start, apt.clientName, apt.professionalName);
            dedupSet.add(key);
        }

        const clientIdCache = new Map<string, string>();
        // Pre-populate cache with already-known client IDs
        for (const row of toImport) {
            if (row.clientId && row.clientName) {
                clientIdCache.set(normalizeHeader(row.clientName), row.clientId);
            }
        }

        let localCreated = 0;
        let localSkipped = 0;
        const localErrors: ImportError[] = [];
        let batchAppointments: AppointmentData[] = [];
        let batchRows: ParsedAppointmentRow[] = [];
        let processedCount = 0;

        const flushBatch = async () => {
            if (batchAppointments.length === 0) return;
            try {
                const batchResult = await createAppointmentsAsync(batchAppointments, true);
                for (let j = 0; j < batchResult.results.length; j++) {
                    const item = batchResult.results[j];
                    if (item.errorMessage) {
                        localErrors.push({
                            rowIndex: batchRows[j].rowIndex,
                            clientName: batchRows[j].clientName,
                            message: item.errorMessage,
                            appointmentData: batchAppointments[j],
                        });
                    } else {
                        localCreated++;
                    }
                }
            } catch (e: any) {
                const message = e?.data?.message ?? e?.message ?? 'Unknown error';
                for (let j = 0; j < batchRows.length; j++) {
                    localErrors.push({
                        rowIndex: batchRows[j].rowIndex,
                        clientName: batchRows[j].clientName,
                        message,
                        appointmentData: batchAppointments[j],
                    });
                }
            }
            batchAppointments = [];
            batchRows = [];
            setCreated(localCreated);
            setSkipped(localSkipped);
            setErrors([...localErrors]);
            setProcessed(processedCount);
        };

        for (let i = 0; i < toImport.length; i++) {
            if (cancelledRef.current) break;

            const row = toImport[i];

            // Dedup check
            if (row.date && row.time) {
                const key = makeDedupKey(row.date, row.time, row.clientName, row.professionalName);
                if (dedupSet.has(key)) {
                    localSkipped++;
                    processedCount++;
                    setSkipped(localSkipped);
                    setProcessed(processedCount);
                    continue;
                }
            }

            // Create new client if needed
            let clientId = row.clientId;
            if (row.isNewClient && row.clientName) {
                const normName = normalizeHeader(row.clientName);
                const cached = clientIdCache.get(normName);
                if (cached) {
                    clientId = cached;
                } else {
                    setCurrentAction(`${t(TK.importAppointmentsCreatingClient)}: ${row.clientName}`);
                    try {
                        const created = await createClientAsync({ name: row.clientName });
                        clientId = created.id;
                        clientIdCache.set(normName, clientId);
                    } catch (e: any) {
                        const message = e?.data?.message ?? e?.message ?? 'Failed to create client';
                        localErrors.push({ rowIndex: row.rowIndex, clientName: row.clientName, message });
                        processedCount++;
                        setErrors([...localErrors]);
                        setProcessed(processedCount);
                        continue;
                    }
                    setCurrentAction('');
                }
            }

            if (!row.professionalId || !row.date || !row.time) {
                processedCount++;
                setProcessed(processedCount);
                continue;
            }

            const endTime = resolveEndTime(row.time, row.endTime, row.serviceKey, options, gs);
            const room = resolveRoom(row.serviceKey, options, gs, row.room);

            const appointmentData: AppointmentData = {
                id: '',
                clientId: clientId ?? undefined,
                clientName: row.clientName ?? undefined,
                professionalId: row.professionalId,
                professionalName: row.professionalName ?? undefined,
                type: row.serviceKey ?? row.serviceLabel ?? '',
                room,
                date: row.date,
                start: row.time,
                end: endTime,
                price: row.price ?? 0,
                vat: 0,
                notes: row.notes ?? undefined,
                hasHappened: row.hasHappened,
                payments: [],
                expenses: [],
                confirmationRequestsSentBySms: [],
                confirmationRequestsSentByWhatsapp: [],
                creationDate: new Date().toISOString(),
            };

            batchAppointments.push(appointmentData);
            batchRows.push(row);
            processedCount++;

            if (batchAppointments.length >= BATCH_SIZE) {
                await flushBatch();
            }
        }

        if (!cancelledRef.current) {
            await flushBatch();
        }

        setProcessed(processedCount);
        setCreated(localCreated);
        setSkipped(localSkipped);
        setErrors([...localErrors]);
        setPhase(cancelledRef.current ? 'cancelled' : 'done');
    }

    const handleCancel = () => {
        cancelledRef.current = true;
    };

    const handleRetry = async (err: ImportError) => {
        if (!err.appointmentData) return;
        setRetrying(err.rowIndex);
        try {
            const batchResult = await createAppointmentsAsync([err.appointmentData], true);
            const item = batchResult.results[0];
            if (!item?.errorMessage) {
                setErrors(prev => prev.filter(e => e.rowIndex !== err.rowIndex));
                setCreated(prev => prev + 1);
            } else {
                setErrors(prev => prev.map(e => e.rowIndex === err.rowIndex ? { ...e, message: item.errorMessage! } : e));
            }
        } catch (e: any) {
            const message = e?.data?.message ?? e?.message ?? 'Unknown error';
            setErrors(prev => prev.map(e => e.rowIndex === err.rowIndex ? { ...e, message } : e));
        }
        setRetrying(null);
    };

    const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;
    const isDone = phase === 'done' || phase === 'cancelled';

    const phaseColor = phase === 'done' ? 'var(--appGreen)' : phase === 'cancelled' ? 'var(--text-muted)' : 'var(--appBlue)';
    const phaseLabel = phase === 'done'
        ? t(TK.importAppointmentsDone)
        : phase === 'cancelled'
        ? t(TK.importAppointmentsCancelled)
        : t(TK.importAppointmentsImporting);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                {!isDone && (
                    <span className="spinner-border spinner-border-sm" style={{ color: 'var(--appBlue)', flexShrink: 0 }} />
                )}
                <span style={{ fontWeight: 600, color: phaseColor }}>{phaseLabel}</span>
                {currentAction && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 'var(--spacer-XS)' }}>
                        {currentAction}
                    </span>
                )}
            </div>

            {/* Progress bar */}
            <div>
                <Progress
                    value={progressPct}
                    color={phase === 'done' ? 'success' : phase === 'cancelled' ? 'secondary' : 'primary'}
                    style={{ height: '10px' }}
                />
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 'var(--spacer-XXS)' }}>
                    {processed} / {total}
                </div>
            </div>

            {/* Live counters */}
            <div style={{ display: 'flex', gap: 'var(--spacer-M)', flexWrap: 'wrap' }}>
                <Counter label={t(TK.importAppointmentsCreated)} value={created} color="var(--appGreen)" />
                <Counter label={t(TK.importAppointmentsPreviewSkipCount)} value={skipped} color="var(--text-muted)" />
                <Counter label={t(TK.importAppointmentsErrors)} value={errors.length} color={errors.length > 0 ? 'var(--danger-color)' : 'var(--text-muted)'} />
            </div>

            {/* Error list */}
            {errors.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '40vh', overflowY: 'auto' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--danger-color)', margin: 0 }}>
                        {t(TK.importAppointmentsErrors)}
                    </p>
                    {errors.slice(0, 50).map(err => (
                        <div
                            key={err.rowIndex}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-XS)',
                                fontSize: '0.8rem',
                                padding: 'var(--spacer-XXS) var(--spacer-XS)',
                                background: '#fff5f5',
                                borderRadius: '4px',
                                border: 'var(--border-thin) solid var(--danger-color)',
                                color: 'var(--danger-color)',
                            }}
                        >
                            <span style={{ flexShrink: 0 }}>
                                <strong>{`#${err.rowIndex + 1}`}</strong>
                                {err.clientName ? ` ${err.clientName}` : ''}
                            </span>
                            <span style={{ flex: 1 }}>{err.message}</span>
                            {err.appointmentData && isDone && (
                                <Button
                                    size="sm"
                                    color="danger"
                                    outline
                                    disabled={retrying === err.rowIndex}
                                    onClick={() => handleRetry(err)}
                                    style={{ flexShrink: 0, fontSize: '0.75rem', padding: '1px var(--spacer-XS)' }}
                                >
                                    {retrying === err.rowIndex
                                        ? <span className="spinner-border spinner-border-sm" />
                                        : t(TK.retry)}
                                </Button>
                            )}
                        </div>
                    ))}
                    {errors.length > 50 && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: 'var(--spacer-XXS) 0' }}>
                            {t(TK.importAppointmentsAndNMore).replace('{n}', String(errors.length - 50))}
                        </span>
                    )}
                </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacer-XS)', marginTop: 'var(--spacer-XS)' }}>
                {!isDone && (
                    <Button color="secondary" onClick={handleCancel}>
                        {t(TK.cancel)}
                    </Button>
                )}
                {isDone && (
                    <Button color="success" onClick={onClose}>
                        {t(TK.close)}
                    </Button>
                )}
            </div>
        </div>
    );
};

interface CounterProps {
    label: string;
    value: number;
    color: string;
}

const Counter: React.FC<CounterProps> = ({ label, value, color }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '80px' }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</span>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
    </div>
);

function makeDedupKey(
    date: string | null | undefined,
    start: string | null | undefined,
    clientName: string | null | undefined,
    professionalName: string | null | undefined,
): string {
    return [
        date ?? '',
        start ?? '',
        normalizeHeader(clientName ?? ''),
        normalizeHeader(professionalName ?? ''),
    ].join('|');
}

export default AppointmentImportStep;
